import { c as _c } from "react/compiler-runtime";
import { BedSolid, BusPictogram, FerryPictogram, InfoCircleSolid, OvernightBus, RoutePictogram, SleeperFerryPictogram, SleeperTrainPictogram, Taxi, TicketSolid, TrainPictogram, Walking } from '@byway/icons';
import { format, parseISO } from 'date-fns';
import { useTranslations } from 'next-intl';
import { Fragment, type ReactNode } from 'react';
import { formatDurationHM } from 'utils/DateFormats';
import { Divider } from '~/components/Divider';
import { ChangeTravelTimesField } from '~/components/TripOverview/transportPage/ChangeTravelTimesField';
import { visuallyHidden } from '~/components/Utilities';
import { getTicketsIncludedKey, hasPrivateCabin, transportModeKeys } from '~/constants/transport-modes';
import type { TripSection } from '~/stores/tpv2/journeyStore';
import type { LegTransformed, Transport } from '~/types/JourneyItem.types';
import { ItineraryCard } from './ItineraryCard';
import styles from './LegCard.module.css';
import { useSleeperTag } from './useSleeperTag';
type LegCardProps = LegTransformed & {
  section: TripSection;
  dayNumber?: number;
};
const renderSup = (chunks: ReactNode) => <sup>{chunks}</sup>;
const MODE_ICON: Partial<Record<Transport, typeof TrainPictogram>> = {
  RAIL: TrainPictogram,
  TRAIN: TrainPictogram,
  TRAM: TrainPictogram,
  JOURNEY: TrainPictogram,
  SLEEPER: SleeperTrainPictogram,
  BUS: BusPictogram,
  COACH: BusPictogram,
  OVERNIGHT_BUS: OvernightBus,
  FERRY: FerryPictogram,
  OVERNIGHT_FERRY: SleeperFerryPictogram,
  TAXI: Taxi,
  WALK: Walking
};
function formatDate(dateStr: string): string {
  try {
    return format(parseISO(dateStr), 'd MMM yy');
  } catch {
    return dateStr;
  }
}
function formatStationLabel(city: string, terminalId: string | null): string {
  return terminalId ? `${city} (${terminalId.split('-')[1]?.toLowerCase() ?? terminalId})` : city;
}
const LegCard = t0 => {
  const $ = _c(116);
  const {
    itemId,
    origin,
    destination,
    viaStops,
    startDate,
    endDate,
    departureTimeText,
    arrivalTimeText,
    duration,
    fromTerminalId,
    fromTerminalName,
    toTerminalId,
    toTerminalName,
    interrail,
    channel,
    modeOfTransports,
    transportClass,
    transportClassLabel,
    images,
    description,
    section,
    dayNumber
  } = t0;
  const t = useTranslations("journey");
  const tRoot = useTranslations();
  const tAria = useTranslations("aria");
  let t1;
  if ($[0] !== viaStops) {
    t1 = viaStops ?? [];
    $[0] = viaStops;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== destination || $[3] !== origin || $[4] !== t1) {
    t2 = [origin, ...t1, destination];
    $[2] = destination;
    $[3] = origin;
    $[4] = t1;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const cities = t2;
  const modes = modeOfTransports.filter(Boolean) as Transport[];
  const displayModes = modes.length > 0 ? modes : ["RAIL"] as Transport[];
  const showTickets = interrail || channel === "RAIL";
  let t3;
  if ($[6] !== viaStops) {
    t3 = viaStops ?? [];
    $[6] = viaStops;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const ticketsIncludedKey = getTicketsIncludedKey(modes, t3.length > 0);
  const sleeperTag = useSleeperTag(modes);
  const showClassTag = section === "transport";
  const isFirstClass = transportClass === "FIRST_CLASS";
  let t4;
  if ($[8] !== modes) {
    t4 = hasPrivateCabin(modes);
    $[8] = modes;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const isSleeper = t4;
  const showStandardClassTag = showClassTag && !isFirstClass && !isSleeper && !!transportClassLabel;
  const showPrivateCompartmentTag = section === "transport" && isSleeper;
  const t5 = showTickets && <div className={styles.detailRow}><TicketSolid size="small" decorative={true} /><span>{t(ticketsIncludedKey)}</span>{showClassTag && isFirstClass && <span className={styles.greenTag}><span>{transportClassLabel ?? t.rich("transport_first_class_applied", {
          sup: renderSup
        })}</span></span>}{showStandardClassTag && <span className={styles.greyTag}>{transportClassLabel}</span>}</div>;
  let t6;
  if ($[10] !== showPrivateCompartmentTag || $[11] !== t) {
    t6 = showPrivateCompartmentTag && <div className={styles.detailRow}><BedSolid size="small" decorative={true} /><span>{t("transport_private_compartment_tag")}</span></div>;
    $[10] = showPrivateCompartmentTag;
    $[11] = t;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== t5 || $[14] !== t6) {
    t7 = <>{t5}{t6}</>;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const detailRows = t7;
  if (section === "overview") {
    let t8;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <RoutePictogram size="small" decorative={true} />;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    let t9;
    if ($[17] !== destination || $[18] !== t) {
      t9 = t("itinerary_travel_to", {
        place: destination
      });
      $[17] = destination;
      $[18] = t;
      $[19] = t9;
    } else {
      t9 = $[19];
    }
    let t10;
    if ($[20] !== dayNumber || $[21] !== t) {
      t10 = dayNumber === undefined ? undefined : t("itinerary_day", {
        day: dayNumber
      });
      $[20] = dayNumber;
      $[21] = t;
      $[22] = t10;
    } else {
      t10 = $[22];
    }
    let t11;
    if ($[23] !== t10 || $[24] !== t9) {
      t11 = <ItineraryCard.Header icon={t8} title={t9} meta={t10} />;
      $[23] = t10;
      $[24] = t9;
      $[25] = t11;
    } else {
      t11 = $[25];
    }
    let t12;
    if ($[26] !== cities[0]) {
      t12 = <span className={styles.routeCity}>{cities[0]}</span>;
      $[26] = cities[0];
      $[27] = t12;
    } else {
      t12 = $[27];
    }
    let t13;
    if ($[28] !== cities) {
      t13 = cities.slice(1);
      $[28] = cities;
      $[29] = t13;
    } else {
      t13 = $[29];
    }
    let t14;
    if ($[30] !== cities || $[31] !== displayModes || $[32] !== t13 || $[33] !== tAria) {
      t14 = t13.map((city, index) => {
        const Icon = MODE_ICON[displayModes[index]] ?? TrainPictogram;
        return <Fragment key={cities.slice(0, index + 2).join("\u2192")}><span className={styles.routeConnector} aria-hidden="true"><span className={styles.routeDivider} /><Icon size="small" decorative={true} /><span className={styles.routeDivider} /></span><span className={visuallyHidden}>{` ${tAria("to")} `}</span><span className={styles.routeCity}>{city}</span></Fragment>;
      });
      $[30] = cities;
      $[31] = displayModes;
      $[32] = t13;
      $[33] = tAria;
      $[34] = t14;
    } else {
      t14 = $[34];
    }
    let t15;
    if ($[35] !== t12 || $[36] !== t14) {
      t15 = <p className={styles.route}>{t12}{t14}</p>;
      $[35] = t12;
      $[36] = t14;
      $[37] = t15;
    } else {
      t15 = $[37];
    }
    let t16;
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <hr className={styles.divider} />;
      $[38] = t16;
    } else {
      t16 = $[38];
    }
    let t17;
    if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
      t17 = <InfoCircleSolid size="small" decorative={true} />;
      $[39] = t17;
    } else {
      t17 = $[39];
    }
    let t18;
    if ($[40] !== t) {
      t18 = t("overview_choose_times");
      $[40] = t;
      $[41] = t18;
    } else {
      t18 = $[41];
    }
    let t19;
    if ($[42] !== t18) {
      t19 = <div className={styles.detailRow}>{t17}<span>{t18}</span></div>;
      $[42] = t18;
      $[43] = t19;
    } else {
      t19 = $[43];
    }
    let t20;
    if ($[44] !== detailRows || $[45] !== t19) {
      t20 = <div className={styles.content}>{detailRows}{t19}</div>;
      $[44] = detailRows;
      $[45] = t19;
      $[46] = t20;
    } else {
      t20 = $[46];
    }
    let t21;
    if ($[47] !== t11 || $[48] !== t15 || $[49] !== t20) {
      t21 = <ItineraryCard variant="leg">{t11}{t15}{t16}{t20}</ItineraryCard>;
      $[47] = t11;
      $[48] = t15;
      $[49] = t20;
      $[50] = t21;
    } else {
      t21 = $[50];
    }
    return t21;
  }
  let t8;
  if ($[51] !== startDate) {
    t8 = formatDate(startDate);
    $[51] = startDate;
    $[52] = t8;
  } else {
    t8 = $[52];
  }
  const formattedDate = t8;
  let t9;
  if ($[53] !== duration) {
    t9 = duration ? formatDurationHM({
      minutes: duration
    }) : "";
    $[53] = duration;
    $[54] = t9;
  } else {
    t9 = $[54];
  }
  const formattedDuration = t9;
  let t10;
  if ($[55] !== fromTerminalId || $[56] !== fromTerminalName || $[57] !== origin) {
    t10 = fromTerminalName ?? formatStationLabel(origin, fromTerminalId);
    $[55] = fromTerminalId;
    $[56] = fromTerminalName;
    $[57] = origin;
    $[58] = t10;
  } else {
    t10 = $[58];
  }
  const originStation = t10;
  let t11;
  if ($[59] !== destination || $[60] !== toTerminalId || $[61] !== toTerminalName) {
    t11 = toTerminalName ?? formatStationLabel(destination, toTerminalId);
    $[59] = destination;
    $[60] = toTerminalId;
    $[61] = toTerminalName;
    $[62] = t11;
  } else {
    t11 = $[62];
  }
  const destinationStation = t11;
  const hasTravelTimes = Boolean(departureTimeText || arrivalTimeText);
  let t12;
  if ($[63] !== cities || $[64] !== tAria) {
    t12 = cities.map((city_0, index_0) => <Fragment key={cities.slice(0, index_0 + 1).join("\u2192")}>{index_0 > 0 && <><span aria-hidden="true"> → </span><span className={visuallyHidden}>{` ${tAria("to")} `}</span></>}{city_0}</Fragment>);
    $[63] = cities;
    $[64] = tAria;
    $[65] = t12;
  } else {
    t12 = $[65];
  }
  const titleContent = t12;
  const PrimaryModeIcon = MODE_ICON[displayModes[0]] ?? TrainPictogram;
  const isTransport = section === "transport";
  const galleryImages = isTransport && images?.length ? images : null;
  let t13;
  if ($[66] !== PrimaryModeIcon || $[67] !== isTransport || $[68] !== titleContent) {
    t13 = isTransport && <ItineraryCard.Header icon={<PrimaryModeIcon size="small" decorative={true} />} title={titleContent} />;
    $[66] = PrimaryModeIcon;
    $[67] = isTransport;
    $[68] = titleContent;
    $[69] = t13;
  } else {
    t13 = $[69];
  }
  let t14;
  if ($[70] !== galleryImages || $[71] !== sleeperTag) {
    t14 = galleryImages && <ItineraryCard.Gallery images={galleryImages} tag={sleeperTag ? <ItineraryCard.Tags tags={[sleeperTag]} /> : undefined} />;
    $[70] = galleryImages;
    $[71] = sleeperTag;
    $[72] = t14;
  } else {
    t14 = $[72];
  }
  const t15 = galleryImages ? `${styles.body} ${styles.bodyBelowGallery}` : styles.body;
  let t16;
  if ($[73] !== isTransport || $[74] !== titleContent) {
    t16 = !isTransport && <p className={styles.title}>{titleContent}</p>;
    $[73] = isTransport;
    $[74] = titleContent;
    $[75] = t16;
  } else {
    t16 = $[75];
  }
  let t17;
  if ($[76] !== displayModes || $[77] !== isTransport || $[78] !== tRoot) {
    t17 = !isTransport && <div className={styles.modes}>{displayModes.map((mode, index_1) => {
        const Icon_0 = MODE_ICON[mode] ?? TrainPictogram;
        return <Fragment key={displayModes.slice(0, index_1 + 1).join("-")}>{index_1 > 0 && <span aria-hidden="true" className={styles.modeArrow}>→</span>}<span className={styles.mode}><Icon_0 size="small" decorative={true} />{transportModeKeys[mode] ? tRoot(transportModeKeys[mode].labelKey) : mode}</span></Fragment>;
      })}</div>;
    $[76] = displayModes;
    $[77] = isTransport;
    $[78] = tRoot;
    $[79] = t17;
  } else {
    t17 = $[79];
  }
  let t18;
  if ($[80] !== hasTravelTimes || $[81] !== isTransport || $[82] !== t) {
    t18 = isTransport && !hasTravelTimes && <ItineraryCard.Tags tags={[{
      label: t("transport_times_tbc_tag")
    }]} />;
    $[80] = hasTravelTimes;
    $[81] = isTransport;
    $[82] = t;
    $[83] = t18;
  } else {
    t18 = $[83];
  }
  let t19;
  if ($[84] !== detailRows || $[85] !== t15 || $[86] !== t16 || $[87] !== t17 || $[88] !== t18) {
    t19 = <div className={t15}>{t16}{t17}{detailRows}{t18}</div>;
    $[84] = detailRows;
    $[85] = t15;
    $[86] = t16;
    $[87] = t17;
    $[88] = t18;
    $[89] = t19;
  } else {
    t19 = $[89];
  }
  let t20;
  if ($[90] !== arrivalTimeText || $[91] !== channel || $[92] !== departureTimeText || $[93] !== description || $[94] !== destination || $[95] !== destinationStation || $[96] !== endDate || $[97] !== formattedDate || $[98] !== formattedDuration || $[99] !== hasTravelTimes || $[100] !== isSleeper || $[101] !== itemId || $[102] !== origin || $[103] !== originStation || $[104] !== section || $[105] !== startDate) {
    t20 = section === "transport" && hasTravelTimes && <ItineraryCard.Content><ItineraryCard.TrainTimes date={formattedDate} arrivalDate={isSleeper && endDate !== startDate ? formatDate(endDate) : undefined} departureTime={departureTimeText} arrivalTime={arrivalTimeText} originStation={originStation} destinationStation={destinationStation} duration={formattedDuration} /><ChangeTravelTimesField itemId={itemId} startDate={startDate} origin={origin} destination={destination} departureTimeText={departureTimeText} arrivalTimeText={arrivalTimeText} isEurostar={channel === "EUROSTAR"} />{description && <Divider variant="horizontal" />}</ItineraryCard.Content>;
    $[90] = arrivalTimeText;
    $[91] = channel;
    $[92] = departureTimeText;
    $[93] = description;
    $[94] = destination;
    $[95] = destinationStation;
    $[96] = endDate;
    $[97] = formattedDate;
    $[98] = formattedDuration;
    $[99] = hasTravelTimes;
    $[100] = isSleeper;
    $[101] = itemId;
    $[102] = origin;
    $[103] = originStation;
    $[104] = section;
    $[105] = startDate;
    $[106] = t20;
  } else {
    t20 = $[106];
  }
  let t21;
  if ($[107] !== description || $[108] !== isTransport) {
    t21 = isTransport && description && <ItineraryCard.Body text={description} />;
    $[107] = description;
    $[108] = isTransport;
    $[109] = t21;
  } else {
    t21 = $[109];
  }
  let t22;
  if ($[110] !== t13 || $[111] !== t14 || $[112] !== t19 || $[113] !== t20 || $[114] !== t21) {
    t22 = <ItineraryCard>{t13}{t14}{t19}{t20}{t21}</ItineraryCard>;
    $[110] = t13;
    $[111] = t14;
    $[112] = t19;
    $[113] = t20;
    $[114] = t21;
    $[115] = t22;
  } else {
    t22 = $[115];
  }
  return t22;
};
export { LegCard };